import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import BaseButton from "@/components/base/button/BaseButton.tsx";
import IconClose from "@/assets/icons/close.svg?react";
import IconCopy from "@/assets/icons/copy.svg?react";
import IconCheck from "@/assets/icons/check.svg?react";
import IconClock from "@/assets/icons/clock.svg?react";
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer.tsx";
import VibratingButton from "@/components/base/button/VibratingButton.tsx";
import showCustomToast from "@/shared/utils/showCustomToast.ts";
import { formatNumber } from "@/shared/utils";
import { sanitizeHtml } from "@/shared/utils/sanitizeHtml";
import { openLink } from "@telegram-apps/sdk-react";
import dayjs from "dayjs";
import type { Voucher } from "../api";
import VoucherHero from "./VoucherHero.tsx";
import HowToUseSheet from "./HowToUseSheet.tsx";
import IconInfo from "@/assets/icons/info-circle-linear.svg?react";

interface VoucherDetailSheetProps {
  voucher: Voucher | null;
  isOpen: boolean;
  onClose: () => void;
  onRedeem?: (voucherId: string) => void;
  isRedeeming?: boolean;
}

export default function VoucherDetailSheet({
  voucher,
  isOpen,
  onClose,
  onRedeem,
  isRedeeming,
}: VoucherDetailSheetProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [howToOpen, setHowToOpen] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  if (!voucher) return null;
  const v = voucher;

  const handleCopyPromo = async () => {
    if (!v.promo_code) return;
    try {
      // clipboard may be unavailable/blocked in Telegram WebView / insecure
      // contexts — only show the "copied" toast when the write succeeds.
      await navigator.clipboard.writeText(v.promo_code);
      showCustomToast(t("copied"), "success");
      setCopied(true);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy promo code:", error);
      showCustomToast(t("copy_failed") || "Nusxalab bo'lmadi", "error");
    }
  };

  const handleOpenLink = () => {
    if (v.link) {
      openLink(v.link, { tryInstantView: false });
    }
  };

  const hasFooter =
    (v.status === "AVAILABLE" && !!onRedeem) ||
    (v.status === "REDEEMED" && !!v.link);

  return (
    <Drawer open={isOpen} onOpenChange={(e) => !e && onClose()}>
      <DrawerContent className="bg-gray-4 max-h-[85vh]" showTopThumb>
        <DrawerHeader className="flex justify-between items-center">
          <DrawerTitle className="text-lg">
            {v.title}
          </DrawerTitle>
          <DrawerClose>
            <IconClose className="size-6 text-gray-1 cursor-pointer" />
          </DrawerClose>
        </DrawerHeader>

        <DrawerBody className="flex-1 overflow-y-auto !p-0">
          <VoucherHero voucher={v} />
          <div className="px-4 pt-4">
          {v.description && (
            <div className="mb-4">
              <div
                className="voucher-richtext text-sm text-white/80 leading-150 font-normal"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(v.description) }}
              />
            </div>
          )}

          {v.promo_code && (
            <div className="w-full mb-4 bg-white/08 border border-dashed border-orange-100/30 rounded-xl px-4 py-3.5 flex-center-between">
              <span className="text-lg font-bold text-orange-100 tracking-widest">
                {v.promo_code}
              </span>
              <VibratingButton
                onClick={handleCopyPromo}
                className="shrink-0 relative size-6"
                vibrationType="success"
              >
                <IconCopy
                  className={`absolute inset-0 size-6 text-gray-1 transition-all duration-300 ease-out ${
                    copied
                      ? "opacity-0 scale-50 rotate-[-20deg]"
                      : "opacity-100 scale-100 rotate-0"
                  }`}
                />
                <IconCheck
                  className={`absolute inset-0 size-6 text-gray-1 transition-all duration-300 ease-out ${
                    copied
                      ? "opacity-100 scale-100 rotate-0"
                      : "opacity-0 scale-50 rotate-[20deg]"
                  }`}
                />
              </VibratingButton>
            </div>
          )}

          {v.how_to_use && (
            <button
              type="button"
              onClick={() => setHowToOpen(true)}
              className="w-full mb-4 flex items-center justify-between gap-3 rounded-xl border border-white/08 bg-white/[0.04] px-4 py-3 active:bg-white/08 transition-colors"
            >
              <div className="flex items-center gap-2">
                <IconInfo className="size-5 text-orange-100" />
                <span className="text-sm font-semibold text-white leading-130">
                  {t("voucher_how_to_use") || "Qanday ishlatish?"}
                </span>
              </div>
              <span className="text-gray-1 text-lg leading-none">›</span>
            </button>
          )}

          {v.details && (
            <div className="rounded-xl bg-white/08 p-3 mb-4">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-orange-100/80 leading-130 mb-2">
                {t("voucher_details") || "Qo'shimcha ma'lumot"}
              </p>
              <div
                className="voucher-richtext text-sm text-white/90 leading-[170%] font-normal"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(v.details) }}
              />
            </div>
          )}

          {v.status === "REDEEMED" && v.redeem_valid_until && (
            <div className="flex items-center gap-1 text-gray-1 mb-4">
              <IconClock className="size-4" />
              <span className="text-xs leading-130">
                {t("voucher_expires_on") || "Amal qilish muddati"}:{" "}
                {dayjs(v.redeem_valid_until).format("DD.MM.YYYY")}
              </span>
            </div>
          )}

          {v.status === "AVAILABLE" && v.end_date && (
            <div className="flex items-center gap-1 text-gray-1 mb-4">
              <IconClock className="size-4" />
              <span className="text-xs leading-130">
                {t("valid_until")}: {dayjs(v.end_date).format("DD.MM.YYYY")}
              </span>
            </div>
          )}
          </div>
        </DrawerBody>

        {hasFooter && (
          <div className="shrink-0 px-4 pt-3 pb-safe-bottom bg-gray-4 border-t border-white/08">
            {v.status === "AVAILABLE" && onRedeem && (
              <BaseButton
                text={`${t("redeem")} — ${formatNumber(v.don_cost)} DON`}
                variant="premium"
                className="w-full !text-dark"
                onClick={() => onRedeem(v.id)}
                isLoading={isRedeeming}
                vibrate
              />
            )}
            {v.status === "REDEEMED" && v.link && (
              <BaseButton
                text={t("open_link")}
                variant="primary"
                className="w-full"
                onClick={handleOpenLink}
                vibrate
              />
            )}
          </div>
        )}

        {howToOpen && (
          <HowToUseSheet
            isOpen={howToOpen}
            onClose={() => setHowToOpen(false)}
            content={v.how_to_use}
          />
        )}
      </DrawerContent>
    </Drawer>
  );
}
